import { useMemo, useState } from 'react';
import { createRootRoute, Outlet, Link } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider } from 'react-intl';
import { Globe } from 'lucide-react';
import { Logo } from '@caratuva/ui';
import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  detectInitialLocale,
  messagesByLocale,
  persistLocale,
  type Locale,
} from '../intl/index.ts';

// Single QueryClient at module scope so it survives HMR. Aggressive defaults
// stay tame: the buyer journey is short-lived, refetch-on-focus would
// surprise users mid-flow.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RootLayout() {
  const [locale, setLocale] = useState<Locale>(() => detectInitialLocale());
  const messages = useMemo(() => {
    const fallback = messagesByLocale[DEFAULT_LOCALE];
    const local = messagesByLocale[locale];
    return { ...fallback, ...local };
  }, [locale]);

  function onLocaleChange(next: Locale): void {
    setLocale(next);
    persistLocale(next);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
        onError={() => {
          /* missing translations are scaffolded; suppress dev noise */
        }}
      >
        <div className="min-h-full flex flex-col">
          <header className="border-b border-[var(--color-navy-100)] bg-white">
            <div className="mx-auto max-w-3xl flex items-center justify-between px-4 py-3">
              <Link to="/" aria-label="Caratuva home">
                <Logo size={22} />
              </Link>
              <label className="flex items-center gap-2 text-sm text-[var(--color-navy-700)]">
                <Globe size={16} strokeWidth={1.5} aria-hidden="true" />
                <span className="sr-only">{LOCALE_LABELS[locale]}</span>
                <select
                  value={locale}
                  onChange={(e) => onLocaleChange(e.target.value as Locale)}
                  className="bg-transparent border border-[var(--color-navy-200)] rounded-md px-2 py-1"
                >
                  {SUPPORTED_LOCALES.map((l) => (
                    <option key={l} value={l}>
                      {LOCALE_LABELS[l]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </header>
          <main className="flex-1">
            <Outlet />
          </main>
          <footer className="border-t border-[var(--color-navy-100)] bg-white">
            <div className="mx-auto max-w-3xl px-4 py-3 text-xs text-[var(--color-navy-500)]">
              Cross-border B2B payments. Settled via Caratuva.
            </div>
          </footer>
        </div>
      </IntlProvider>
    </QueryClientProvider>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
});
